
import React, { useState, useEffect } from 'react';
import { Crown, X, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getActivePriceDisplay, FOUNDERS_PRICING } from '../stripe-config';
import { usePayment } from '../hooks/usePayment';
import { supabase } from '../lib/supabase';

interface CompactTrialBannerProps {
  daysLeft: number;
  onDismiss: () => void;
}

export function CompactTrialBanner({ daysLeft, onDismiss }: CompactTrialBannerProps) {
  const navigate = useNavigate();
  const pricing = getActivePriceDisplay();
  const [countdown, setCountdown] = useState('');

  useEffect(() => {
    if (!pricing.isFounders) return;

    const tick = () => {
      const diff = FOUNDERS_PRICING.endDate.getTime() - Date.now();
      if (diff <= 0) { setCountdown(''); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setCountdown(`${d}d ${h}h ${m}m`);
    };

    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [pricing.isFounders]);

  const { handlePayment, loading: paymentLoading } = usePayment();

  const handleUpgradeClick = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const result = await handlePayment({ interval: 'monthly', email: user?.email || '' });
      if (result?.url) window.location.href = result.url;
    } catch {
      navigate('/dashboard/settings');
    }
  };

  return (
    <div className="bg-gradient-to-r from-electric to-blue-600 text-white p-3 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Crown className="h-4 w-4 mr-2" />
          <span className="text-sm font-medium">
            {daysLeft === 1 ? 'Trial ends tomorrow!' : `${daysLeft} days left in trial`}
            {pricing.isFounders && countdown ? <span className="ml-1 font-mono text-xs opacity-80">· Founders ends {countdown}</span> : null}
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={handleUpgradeClick}
            disabled={paymentLoading}
            className="bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded text-sm font-medium transition-colors flex items-center disabled:opacity-60"
          >
            <CreditCard className="h-3 w-3 mr-1" />
            {paymentLoading ? 'Loading…' : 'Upgrade'}
          </button>
          <button
            onClick={onDismiss}
            className="text-white/80 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
